import { omit } from "lodash";
import { useSnackbar } from "notistack";
import { useState } from "react";
import {
  BillingMethod,
  BillingRuleConditionOperator,
  BillingRuleConditionTarget,
  RateTableEntityType,
  RateTableType,
  useAddRateTableMutation,
  useDeleteRateTableMutation,
  useEditRateTableMutation,
  useGetCustomerDetailsQuery,
  useGetRateTablesQuery,
} from "../../../graphql/generated";
import ErrorMessage from "../../common/ErrorMessage/ErrorMessage";
import LoadingOverlay from "../../common/LoadingOverlay";
import { defaultCopyEffectiveDates } from "../EffectiveDates/defaultCopyEffectiveDates";
import RatingTableEditor, { RatingTableItem } from "./RatingTableEditor";
import RatingTablesList from "./RatingTablesList";

type RatingTableEditorContainerProps = {
  entityType: RateTableEntityType;
  entityId: string;
};

const RatingTableEditorContainer = ({
  entityType,
  entityId,
}: RatingTableEditorContainerProps) => {
  const [editingTable, setEditingTable] =
    useState<RatingTableItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const getRatingTablesQuery = useGetRateTablesQuery(
    {
      entityType,
      entityId,
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const getCustomerQuery = useGetCustomerDetailsQuery(
    {
      id: entityId,
    },
    {
      enabled: entityType === RateTableEntityType.Customer,
    }
  );

  const addRatingTableMutation = useAddRateTableMutation();
  const editRatingTableMutation = useEditRateTableMutation();
  const deleteRatingTableMutation = useDeleteRateTableMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingTable({
      _id: "NEW",
      label: "",
      description: "",
      referenceNumber: "",
      priority: 0,
      rateType: RateTableType.Primary,
      isActive: true,
      rowAxis: {
        conditions: [
          {
            target: BillingRuleConditionTarget.TrailerType,
            operator: BillingRuleConditionOperator.Equals,
            value: null,
            parameters: [],
          },
        ],
      },
      columnAxis: null,
      cells: [
        [
          {
            rates: [
              {
                method: BillingMethod.FlatFee,
                amount: 0,
                label: "",
                referenceNumber: "",
                parameters: [],
              },
            ],
          },
        ],
      ],
    });
  };

  const handleEdit = (table: RatingTableItem) => {
    setIsCreating(false);
    setEditingTable(table);
  };

  const handleCopy = (table: RatingTableItem) => {
    setIsCreating(true);
    setEditingTable({
      ...table,
      _id: "NEW",
      label: table.label ? `${table.label} (copy)` : "(copy)",
      ...defaultCopyEffectiveDates(),
    });
  };

  const handleSave = async () => {
    if (!editingTable) return;

    try {
      if (isCreating) {
        await addRatingTableMutation.mutateAsync({
          newRateTableData: {
            label: editingTable.label,
            description: editingTable.description,
            referenceNumber: editingTable.referenceNumber,
            priority: editingTable.priority,
            rateType: editingTable.rateType,
            isActive: editingTable.isActive,
            effectiveStartDate: editingTable.effectiveStartDate,
            effectiveEndDate: editingTable.effectiveEndDate,
            rowAxis: editingTable.rowAxis,
            columnAxis: editingTable.columnAxis,
            cells: editingTable.cells,
            entityType,
            entityId,
          },
        });
      } else {
        await editRatingTableMutation.mutateAsync({
          id: editingTable._id,
          editRateTableData: omit(editingTable, "_id"),
        });
      }
      enqueueSnackbar("Rating table saved successfully", {
        variant: "success",
      });
      setEditingTable(null);
      setIsCreating(false);
      getRatingTablesQuery.refetch();
    } catch (error) {
      enqueueSnackbar("Failed to save rating table", { variant: "error" });
    }
  };

  const handleDelete = async (tableId: string) => {
    try {
      await deleteRatingTableMutation.mutateAsync({ id: tableId });
      enqueueSnackbar("Rating table deleted successfully", {
        variant: "success",
      });
      getRatingTablesQuery.refetch();
    } catch (error) {
      enqueueSnackbar("Failed to delete rating table", { variant: "error" });
    }
  };

  const handleCancel = () => {
    setEditingTable(null);
    setIsCreating(false);
  };

  const customer = getCustomerQuery.data?.customerById;

  if (
    getRatingTablesQuery.isLoading ||
    (entityType === RateTableEntityType.Customer && getCustomerQuery.isLoading)
  ) {
    return <LoadingOverlay loading />;
  }

  if (getRatingTablesQuery.error || !getRatingTablesQuery.data) {
    return (
      <ErrorMessage
        error={
          getRatingTablesQuery.error ||
          "Unknown error while fetching rating tables"
        }
      />
    );
  }

  const isSaving =
    addRatingTableMutation.isLoading ||
    editRatingTableMutation.isLoading ||
    deleteRatingTableMutation.isLoading;

  if (editingTable) {
    return (
      <>
        <ErrorMessage
          error={
            addRatingTableMutation.error ||
            editRatingTableMutation.error ||
            deleteRatingTableMutation.error
          }
        />
        <RatingTableEditor
          ratingTable={editingTable}
          customer={customer}
          onChange={setEditingTable}
          onSave={handleSave}
          onCancel={handleCancel}
          saving={isSaving}
          // Settlement tables make trip payments, not invoice lines
          showTaxable={entityType === RateTableEntityType.Customer}
        />
      </>
    );
  }

  return (
    <>
      <ErrorMessage error={deleteRatingTableMutation.error} />
      <RatingTablesList
        ratingTables={getRatingTablesQuery.data.rateTables.data}
        onEdit={handleEdit}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onCreateNew={handleCreateNew}
        deleting={deleteRatingTableMutation.isLoading}
      />
    </>
  );
};

export default RatingTableEditorContainer;
